/**
 * @fileoverview Office phone, email, and website fields for registration contact info.
 * @module components/AppointmentRegistrationSections/ContactInformation/RegistrationContactFields
 */
import { ReactComponent as MailIcon } from "../../../assets/icons/ContactInformation/MailIcon.svg";
import { ReactComponent as WebsiteIcon } from "../../../assets/icons/ContactInformation/WebsiteIcon.svg";
import CountryCodeSelector from "../../CountryCodeSelector";
import {  getCountryCodeOptions  } from "../../../data/selectOptions";
import { getSelectedCountryCodeOption } from "../../../utils/common";

const WEBSITE_MAX_LENGTH = 2083;
const EMAIL_MAX_LENGTH = 100;

const showsRequired = (missingRequiredFields, fieldKey) =>
  Boolean(
    missingRequiredFields && Object.hasOwn(missingRequiredFields, fieldKey),
  );

const hasExternalError = (getFieldValidation, fieldKey) =>
  Boolean(getFieldValidation && getFieldValidation(fieldKey));

export const RegistrationOfficePhoneField = ({
  company,
  styles,
  form,
  missingRequiredFields,
  getFieldValidation,
}) => {
  if (!company || !Object.hasOwn(company, "telefonoDeOficina")) {
    return null;
  }

  const countryCodeOptions = getCountryCodeOptions(form.t);
  const invalid =
    form.getFieldValidationState(
      "telefonoDeOficina",
      form.telefonoDeOficina,
    ) === "error" || hasExternalError(getFieldValidation, "telefonoDeOficina");

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {form.t("components.contactInformation.officePhoneLabel")}
        {showsRequired(missingRequiredFields, "telefonoDeOficina") && (
          <span className={styles.required}> *</span>
        )}
      </label>
      <div
        className={`${styles.phoneInputContainer} ${
          invalid ? styles.inputError : ""
        }`}
      >
        <CountryCodeSelector
          value={getSelectedCountryCodeOption(
            countryCodeOptions,
            form.selectedCountryCode,
          )}
          onChange={form.handleCountryCodeChange}
          containerWidth={form.countryCodeContainerWidth}
          placeholder={form.t(
            "components.contactInformation.countryCodeSelectorPlaceholder",
          )}
        />
        <input
          type="tel"
          className={styles.input}
          placeholder={form.t(
            "components.contactInformation.officePhonePlaceholder",
          )}
          value={form.telefonoDeOficina}
          onChange={(event) => form.handleTelefonoChange(event.target.value)}
        />
      </div>
      {form.getErrorMessage("telefonoDeOficina") && (
        <p className={styles.errorMessage}>
          {form.getErrorMessage("telefonoDeOficina")}
        </p>
      )}
    </div>
  );
};

export const RegistrationEmailField = ({
  company,
  styles,
  form,
  missingRequiredFields,
  getFieldValidation,
}) => {
  if (!company || !Object.hasOwn(company, "correoElectronicoPrincipal")) {
    return null;
  }

  const invalid =
    form.getFieldValidationState(
      "correoElectronicoPrincipal",
      form.correoElectronicoPrincipal,
    ) === "error" ||
    hasExternalError(getFieldValidation, "correoElectronicoPrincipal");

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {form.t("components.contactInformation.mainEmailAddressLabel")}
        {showsRequired(missingRequiredFields, "correoElectronicoPrincipal") && (
          <span className={styles.required}> *</span>
        )}
      </label>
      <div className={styles.inputWrapper}>
        <MailIcon
          className={`${styles.inputIcon} ${
            form.isTablet ? styles.tabletInputIcon : ""
          }`}
        />
        <input
          type="email"
          className={`${styles.input} ${styles.inputWithIcon} ${
            invalid ? styles.inputError : ""
          }`}
          placeholder={form.t(
            "components.contactInformation.mainEmailAddressPlaceholder",
          )}
          value={form.correoElectronicoPrincipal}
          onChange={(event) => form.handleEmailChange(event.target.value)}
          maxLength={EMAIL_MAX_LENGTH}
        />
      </div>
      {form.getErrorMessage("correoElectronicoPrincipal") && (
        <p className={styles.errorMessage}>
          {form.getErrorMessage("correoElectronicoPrincipal")}
        </p>
      )}
    </div>
  );
};

export const RegistrationWebsiteField = ({
  company,
  styles,
  form,
  missingRequiredFields,
  getFieldValidation,
}) => {
  if (!company || !Object.hasOwn(company, "paginaInternet")) {
    return null;
  }

  const invalid =
    form.getFieldValidationState("paginaInternet", form.paginaInternet) ===
      "error" || hasExternalError(getFieldValidation, "paginaInternet");

  return (
    <div className={styles.inputRow}>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>
          {form.t("components.contactInformation.websiteLabel")}
          {showsRequired(missingRequiredFields, "paginaInternet") && (
            <span className={styles.required}> *</span>
          )}
        </label>
        <div className={styles.inputWrapper}>
          <WebsiteIcon className={styles.inputIcon} />
          <input
            type="text"
            className={`${styles.webpageInput} ${styles.webpageInputWithIcon} ${
              form.isTablet || form.isPhone ? styles.tabletInputIcon : ""
            } ${invalid ? styles.inputError : ""}`}
            placeholder={form.t(
              "components.contactInformation.websitePlaceholder",
            )}
            value={form.paginaInternet}
            onChange={(event) => form.handleWebsiteChange(event.target.value)}
            maxLength={WEBSITE_MAX_LENGTH}
          />
        </div>
        {form.getErrorMessage("paginaInternet") && (
          <p className={styles.errorMessage}>
            {form.getErrorMessage("paginaInternet")}
          </p>
        )}
      </div>
    </div>
  );
};
